/**
 * Client-side capture of Google Business Profile clicks.
 *
 * Google strips the referrer from the profile's website and service buttons, so
 * the only reliable signal is the utm_campaign tag on the link itself. Every
 * landing with a `gbp-` campaign is recorded once per visit, per page, so the
 * internal dashboard can show which page each profile is actually feeding.
 */
import { recordGbpVisit } from "@/lib/gbp-visits.functions";

const KEY = "csl_gbp_logged_v1";

function profileFor(campaign: string): string {
  if (campaign.includes("golden-beach")) return "Golden Beach";
  if (campaign.includes("scarborough")) return "Scarborough";
  return "Both profiles";
}

export function recordGbpClick(): void {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  const campaign = (params.get("utm_campaign") ?? "").toLowerCase();
  if (!campaign.startsWith("gbp")) return;

  const pagePath = window.location.pathname || "/";
  const stamp = `${campaign}|${params.get("utm_content") ?? ""}|${pagePath}`;

  try {
    const seen = JSON.parse(window.sessionStorage.getItem(KEY) ?? "[]") as string[];
    if (seen.includes(stamp)) return;
    window.sessionStorage.setItem(KEY, JSON.stringify([...seen, stamp].slice(-40)));
  } catch {
    // sessionStorage unavailable — still record the click.
  }

  void recordGbpVisit({
    data: {
      pagePath,
      profile: profileFor(campaign),
      utmCampaign: campaign,
      utmContent: params.get("utm_content") ?? "",
      utmSource: params.get("utm_source") ?? "",
      utmMedium: params.get("utm_medium") ?? "",
      referrer: document.referrer ? document.referrer.slice(0, 300) : "",
    },
  }).catch(() => {
    // Reporting must never interfere with the visit.
  });
}
